import PropTypes from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import NavigationBlocker from './NavigationBlocker';

const NavigationBlockerContext = createContext(null);

export const NavigationBlockerProvider = ({ children }) => {
    const [isBlocking, setIsBlocking] = useState(false);
    const [onNavigate, setOnNavigate] = useState(null);
    const skipNextNavigationRef = useRef(false);

    const register = useCallback((blocking, navigateCallback) => {
        setIsBlocking(blocking);
        setOnNavigate(() => navigateCallback);
    }, []);

    const unregister = useCallback(() => {
        setIsBlocking(false);
        setOnNavigate(null);
    }, []);

    const allowNextNavigation = useCallback(() => {
        skipNextNavigationRef.current = true;
    }, []);

    const value = useMemo(() => ({ register, unregister, allowNextNavigation }), [
        register,
        unregister,
        allowNextNavigation,
    ]);

    return (
        <NavigationBlockerContext.Provider value={value}>
            {children}
            <NavigationBlocker
                isBlocking={isBlocking}
                onNavigate={onNavigate || undefined}
                skipNextNavigationRef={skipNextNavigationRef}
            />
        </NavigationBlockerContext.Provider>
    );
};

NavigationBlockerProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useNavigationBlocker = (isBlocking, onNavigate) => {
    const context = useContext(NavigationBlockerContext);

    useEffect(() => {
        if (!context) return undefined;

        context.register(isBlocking, onNavigate);

        return () => context.unregister();
    }, [context, isBlocking, onNavigate]);

    return { allowNextNavigation: context?.allowNextNavigation || (() => {}) };
};
